import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {apiPostZoneStats} from "./Actions";
import {Form} from "react-final-form";
import moment from "moment";
import {format, eachDayOfInterval} from "date-fns";
import DateTimeField from "../../../FinalForm/DatePickerField";
import {FiRefreshCcw} from "react-icons/fi";
import {DefaultLoader} from "../../../components/DefaultLoader";
import {FilterButton} from "../components/Button";
import PageHeader from "../components/PageHeader";
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
    CartesianGrid, Tooltip, Legend
} from "recharts";

// ─── shared styles ────────────────────────────────────────────────────────────

const cardStyle = {
    border:"1px solid #e2e8f0",borderRadius:"10px",background:"#fff",
    padding:"14px",display:"flex",flexDirection:"column",gap:"8px"
};
const titleStyle = {
    fontSize: "var(--enigoo-fs-md)",fontWeight:700,color:"#1e293b",
    borderBottom:"1px solid #f1f5f9",paddingBottom:"6px"
};
const rowStyle    = {display:"flex",justifyContent:"space-between",alignItems:"center"};
const labelStyle  = {fontSize: "var(--enigoo-fs-xs)",color:"#64748b"};
const valueStyle  = {fontSize: "var(--enigoo-fs-lg)",fontWeight:700};
const gridStyle   = {display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:"10px"};
const sectionTitleStyle = {fontSize: "var(--enigoo-fs-lg)",fontWeight:700,color:"#475569",marginBottom:"8px"};

const fmtDay = (d) => { const [y,m,day] = d.split("-"); return `${day}. ${m}. ${y}`; };

// ─── custom tooltip ───────────────────────────────────────────────────────────

const CustomTooltip = ({active, payload, label}) => {
    if (!active || !payload?.length) return null;
    return (
        <div style={{background:"#fff",border:"1px solid #e2e8f0",borderRadius:"8px",padding:"10px 14px",fontSize: "var(--enigoo-fs-sm)",boxShadow:"0 2px 8px rgba(0,0,0,.08)"}}>
            <div style={{fontWeight:700,color:"#1e293b",marginBottom:"6px"}}>{label}</div>
            {payload.map(p => (
                <div key={p.dataKey} style={{display:"flex",justifyContent:"space-between",gap:"16px",color:p.color}}>
                    <span>{p.name}</span>
                    <span style={{fontWeight:700}}>{p.value}</span>
                </div>
            ))}
        </div>
    );
};

// ─── tab styles (same as Turnstile.js) ───────────────────────────────────────

const tabActive   = {background:"#2563eb",color:"#fff",border:"none",borderRadius:"7px 7px 0 0",padding:"7px 14px",fontSize: "var(--enigoo-fs-xs)",fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:"5px",letterSpacing:"0.05em",textTransform:"uppercase"};
const tabInactive = {background:"#e2e8f0",color:"#64748b",border:"none",borderRadius:"7px 7px 0 0",padding:"7px 14px",fontSize: "var(--enigoo-fs-xs)",fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:"5px",letterSpacing:"0.05em",textTransform:"uppercase"};
const tabBody     = {background:"#fff",border:"1px solid #e2e8f0",borderRadius:"0 8px 8px 8px",padding:"12px",boxShadow:"0 1px 3px rgba(0,0,0,0.05)"};

// ─── main ─────────────────────────────────────────────────────────────────────

const TABS = [
    {key:"prehled", label:"Celkový přehled"},
    {key:"grafy",   label:"Grafy"},
];

const ZoneStats = () => {
    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(false);
    const [days, setDays]       = useState([]);
    const [tab, setTab]         = useState("prehled");
    const user = useSelector(s => s.user);

    useEffect(() => {
        if (user?.activeAreal?.id && days.length > 0) getData();
    }, [user.activeAreal, days]);

    const getData = () => {
        setLoading(true);
        apiPostZoneStats(user.activeAreal.id, {type: 0, days}, (response) => {
            setData(response);
            setLoading(false);
        }, () => {
            setData(null);
            setLoading(false);
        });
    };

    const zoneNames = data?.length > 0
        ? [...new Set(data.flatMap(d => Object.keys(d.zonesData || {})))]
        : [];

    const totals = data?.length > 1 ? data.reduce((acc, item) => ({
        inAreal:    acc.inAreal    + (item.inAreal    ?? 0),
        attendence: acc.attendence + (item.attendence ?? 0),
    }), {inAreal: 0, attendence: 0}) : null;

    const ZONE_COLORS = ["#2563eb","#16a34a","#dc2626","#d97706","#7c3aed","#0891b2","#be185d","#065f46","#b45309","#1d4ed8"];

    const chartData = data?.map(item => {
        const entry = {day: fmtDay(item.day)};
        zoneNames.forEach(z => { entry[z] = item.zonesData?.[z]?.attendance ?? 0; });
        return entry;
    }) ?? [];

    return (
        <>
            {user.activeAreal && (
                <>
                    <PageHeader crumbs={[{label: "Statistiky zón"}]}/>

                    {/* date filter */}
                    <div style={{border:"1px solid #e2e8f0",borderRadius:"10px",background:"#fff",marginBottom:"14px",padding:"12px"}}>
                        <Form
                            initialValues={{dateFrom: format(new Date(), "yyyy-MM-dd")}}
                            onSubmit={(values) => {
                                if (values.dateTo) {
                                    const daysInRange = eachDayOfInterval({
                                        start: new Date(values.dateFrom),
                                        end:   new Date(values.dateTo),
                                    });
                                    setDays(daysInRange.map(d => format(d, "yyyy-MM-dd")));
                                } else {
                                    setDays([values.dateFrom]);
                                }
                            }}
                            render={({handleSubmit, values}) => (
                                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:"8px"}}>
                                    <DateTimeField name={"dateFrom"} isMandatory={true} valueToCompare={values.dateTo}
                                        validateFun={(d1, d2) => moment(d1).isAfter(moment(d2)) ? "Den od nemůže být po dni do" : ""}
                                        label={"Den od"} type={"from"} placeholder={"Datum od"} showTimeSelect={false} onEnter={handleSubmit}/>
                                    <DateTimeField name={"dateTo"} isMandatory={false} valueToCompare={values.dateFrom}
                                        validateFun={(d1, d2) => moment(d2).isAfter(moment(d1)) ? "Den od nemůže být po dni do" : ""}
                                        label={"Den do"} type={"to"} placeholder={"Datum do"} showTimeSelect={false} onEnter={handleSubmit}/>
                                    <div style={{display:"flex",alignItems:"flex-end",justifyContent:"flex-end",gridColumn:"1 / -1"}}>
                                        <FilterButton onClick={handleSubmit} disabled={loading} text="Načíst" icon={<FiRefreshCcw size={12}/>}/>
                                    </div>
                                </div>
                            )}
                        />
                    </div>

                    {/* tabs */}
                    {data?.length > 0 && (
                        <div style={{display:"flex",gap:"3px",marginBottom:"0"}}>
                            {TABS.map(t => (
                                <button key={t.key} type="button" onClick={() => setTab(t.key)}
                                    style={tab === t.key ? tabActive : tabInactive}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* body */}
                    {loading ? (
                        <DefaultLoader text={"Načítám statistiky..."}/>
                    ) : data?.length > 0 ? (
                        <div style={tabBody}>
                            {/* ── tab: celkový přehled ── */}
                            {tab === "prehled" && (
                                <>
                                    <div style={{marginBottom:"16px"}}>
                                        <div style={sectionTitleStyle}>Celkový přehled</div>
                                        <div style={gridStyle}>
                                            {data.map((item, i) => (
                                                <div key={i} style={cardStyle}>
                                                    <span style={titleStyle}>{fmtDay(item.day)}</span>
                                                    <div style={rowStyle}>
                                                        <span style={labelStyle}>V areálu</span>
                                                        <span style={{...valueStyle,color:"#2563eb"}}>{item.inAreal}</span>
                                                    </div>
                                                    <div style={rowStyle}>
                                                        <span style={labelStyle}>Celková návštěvnost</span>
                                                        <span style={{...valueStyle,color:"#16a34a"}}>{item.attendence}</span>
                                                    </div>
                                                </div>
                                            ))}
                                            {totals && (
                                                <div style={{...cardStyle,background:"#f8fafc",border:"1px solid #cbd5e1"}}>
                                                    <span style={{...titleStyle,color:"#475569"}}>Celkem ({data.length} dní)</span>
                                                    <div style={rowStyle}>
                                                        <span style={labelStyle}>V areálu (suma)</span>
                                                        <span style={{...valueStyle,color:"#2563eb"}}>{totals.inAreal}</span>
                                                    </div>
                                                    <div style={rowStyle}>
                                                        <span style={labelStyle}>Celková návštěvnost</span>
                                                        <span style={{...valueStyle,color:"#16a34a"}}>{totals.attendence}</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {zoneNames.length > 0 && (
                                        <div>
                                            <div style={sectionTitleStyle}>Statistiky dle zón</div>
                                            <div style={gridStyle}>
                                                {zoneNames.map(zoneName => {
                                                    const totalOccupancy  = data.reduce((s, d) => s + (d.zonesData?.[zoneName]?.occupancy  ?? 0), 0);
                                                    const totalAttendance = data.reduce((s, d) => s + (d.zonesData?.[zoneName]?.attendance ?? 0), 0);
                                                    return (
                                                        <div key={zoneName} style={cardStyle}>
                                                            <span style={titleStyle}>{zoneName}</span>
                                                            {data.map(item => {
                                                                const zd = item.zonesData?.[zoneName];
                                                                return (
                                                                    <div key={item.day} style={{display:"flex",flexDirection:"column",gap:"2px",paddingBottom:"4px",borderBottom:"1px solid #f8fafc"}}>
                                                                        <span style={{...labelStyle,fontWeight:600}}>{fmtDay(item.day)}</span>
                                                                        <div style={rowStyle}>
                                                                            <span style={labelStyle}>V zóně</span>
                                                                            <span style={{...valueStyle,fontSize: "var(--enigoo-fs-md)",color:"#7c3aed"}}>{zd?.occupancy ?? 0}</span>
                                                                        </div>
                                                                        <div style={rowStyle}>
                                                                            <span style={labelStyle}>Návštěvnost</span>
                                                                            <span style={{...valueStyle,fontSize: "var(--enigoo-fs-md)",color:"#16a34a"}}>{zd?.attendance ?? 0}</span>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                            {data.length > 1 && (
                                                                <div style={{display:"flex",flexDirection:"column",gap:"2px",marginTop:"4px",paddingTop:"6px",borderTop:"1px solid #e2e8f0"}}>
                                                                    <span style={{...labelStyle,fontWeight:600}}>Celkem</span>
                                                                    <div style={rowStyle}>
                                                                        <span style={labelStyle}>V zóně</span>
                                                                        <span style={{...valueStyle,fontSize: "var(--enigoo-fs-md)",color:"#7c3aed"}}>{totalOccupancy}</span>
                                                                    </div>
                                                                    <div style={rowStyle}>
                                                                        <span style={labelStyle}>Návštěvnost</span>
                                                                        <span style={{...valueStyle,fontSize: "var(--enigoo-fs-md)",color:"#16a34a"}}>{totalAttendance}</span>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* ── tab: grafy ── */}
                            {tab === "grafy" && (
                                <>
                                    <div style={sectionTitleStyle}>Návštěvnost dle dní</div>
                                    <div style={{width:"100%",height:320}}>
                                        <ResponsiveContainer>
                                            <BarChart data={chartData} margin={{top:10,right:20,left:0,bottom:10}}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                                                <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{fontSize:11,fill:"#64748b"}}/>
                                                <YAxis axisLine={false} tickLine={false} tick={{fontSize:11,fill:"#64748b"}} width={40}/>
                                                <Tooltip content={<CustomTooltip/>} cursor={{fill:"rgba(37,99,235,.05)"}}/>
                                                <Legend verticalAlign="top" height={32} iconType="circle" wrapperStyle={{fontSize: "var(--enigoo-fs-sm)"}}/>
                                                {zoneNames.map((z, i) => (
                                                    <Bar key={z} dataKey={z} fill={ZONE_COLORS[i % ZONE_COLORS.length]} radius={[4,4,0,0]}/>
                                                ))}
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </>
                            )}
                        </div>
                    ) : days.length > 0 ? (
                        <div style={{padding:"40px",textAlign:"center",color:"#94a3b8",fontSize: "var(--enigoo-fs-md)"}}>
                            Žádná data pro vybrané období
                        </div>
                    ) : null}
                </>
            )}
        </>
    );
};

export default ZoneStats;
